import React, { useState } from 'react';
import { useReportCreation } from '../../hooks/useReportCreation';
import DragDropUpload from '../upload/DragDropUpload';
import { useNavigate } from 'react-router-dom';

interface InterviewerInfo {
  linkedinUrl?: string;
  fullName?: string;
  jobTitle?: string;
}

interface JobInfo {
  jobUrl?: string;
  jobDescription?: string;
  companyWebsite?: string;
}

/**
 * Cleans and standardizes job posting URLs from known job platforms
 * Returns the original URL for unknown platforms
 */
export function cleanJobUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    
    // LinkedIn URL cleaning
    if (urlObj.hostname.includes('linkedin.com')) {
      const pathParts = urlObj.pathname.split('/');
      const jobId = pathParts[pathParts.indexOf('view') + 1];
      return `https://www.linkedin.com/jobs/view/${jobId}`;
    }
    
    // Indeed URL cleaning
    if (urlObj.hostname.includes('indeed.com')) {
      const jobKey = urlObj.searchParams.get('jk');
      return `https://www.indeed.com/viewjob?jk=${jobKey}`;
    }
    
    // For all other URLs, return as-is
    return url;
  } catch (error) {
    console.error('Error cleaning URL:', error);
    return url; // Return original URL if cleaning fails
  }
}

export default function NewReportForm({ onNewReport }) {
  const [file, setFile] = useState<File | null>(null);
  const [interviewerMethod, setInterviewerMethod] = useState<'linkedin' | 'manual'>('linkedin');
  const [jobMethod, setJobMethod] = useState<'url' | 'description'>('url');
  const [interviewerInfo, setInterviewerInfo] = useState<InterviewerInfo>({});
  const [jobInfo, setJobInfo] = useState<JobInfo>({});
  const { submitReport, loading, error } = useReportCreation();
  const navigate = useNavigate();

  const inputClasses = "w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-blue-500 focus:ring-blue-500";
  const labelClasses = "block text-sm font-medium text-gray-600 mb-1";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      return;
    }

    // Validate interviewer information
    if (interviewerMethod === 'linkedin' && !interviewerInfo.linkedinUrl) {
      return;
    }
    if (interviewerMethod === 'manual' && 
        (!interviewerInfo.fullName || !interviewerInfo.jobTitle)) {
      return;
    }

    // Validate job information
    if (jobMethod === 'url' && !jobInfo.jobUrl) {
      return;
    }
    if (jobMethod === 'description' && !jobInfo.jobDescription) {
      return;
    }
    if (!jobInfo.companyWebsite) {
      return;
    }

    try {
      await submitReport({
        resumeFile: file,
        linkedinUrl: interviewerMethod === 'linkedin' ? interviewerInfo.linkedinUrl! : '',
        jobUrl: jobMethod === 'url' ? cleanJobUrl(jobInfo.jobUrl!) : '',
        interviewerInfo: interviewerMethod === 'manual' ? {
          fullName: interviewerInfo.fullName!,
          jobTitle: interviewerInfo.jobTitle!
        } : undefined,
        jobDescription: jobMethod === 'description' ? jobInfo.jobDescription! : undefined,
        companyWebsite: jobInfo.companyWebsite!
      });

      // Reset form
      setFile(null);
      setInterviewerInfo({});
      setJobInfo({});
      onNewReport({ /* gather report data */ });

      // Redirect to the dashboard after report submission
      navigate('/dashboard/reports');
    } catch (err) {
      console.error('Failed to submit report:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Section 1: Resume Upload */}
      <div className="relative">
        <div className="absolute -left-12 top-0">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
            1
          </div>
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Resume</h3>
          <DragDropUpload
            onFileSelect={setFile}
            value={file}
            accept=".pdf,.doc,.docx"
            maxSize={10 * 1024 * 1024}
          />
        </div>
      </div>

      {/* Section 2: Interviewer Information */}
      <div className="relative">
        <div className="absolute -left-12 top-0">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
            2
          </div>
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Interviewer Information</h3>
          <div className="space-y-4">
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setInterviewerMethod('linkedin')}
                className={`flex-1 py-2 px-4 rounded-lg border ${
                  interviewerMethod === 'linkedin' 
                    ? 'border-blue-600 bg-blue-50 text-blue-600' 
                    : 'border-gray-200'
                }`}
              >
                LinkedIn Profile
              </button>
              <button
                type="button"
                onClick={() => setInterviewerMethod('manual')}
                className={`flex-1 py-2 px-4 rounded-lg border ${
                  interviewerMethod === 'manual' 
                    ? 'border-blue-600 bg-blue-50 text-blue-600' 
                    : 'border-gray-200'
                }`}
              >
                Manual Entry
              </button>
            </div>

            {interviewerMethod === 'linkedin' ? (
              <div>
                <label htmlFor="linkedin-url" className={labelClasses}>
                  LinkedIn Profile URL
                </label>
                <input
                  id="linkedin-url"
                  type="url"
                  placeholder="https://www.linkedin.com/in/..."
                  value={interviewerInfo.linkedinUrl || ''}
                  onChange={(e) => setInterviewerInfo({ linkedinUrl: e.target.value })}
                  className={inputClasses}
                />
                <div className="mt-2 text-sm text-blue-600 bg-blue-50 p-2 rounded-md">
                  <strong>Tip:</strong> Don't have a LinkedIn URL? Click the "Manual Entry" button above to enter the information directly.
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label htmlFor="full-name" className={labelClasses}>
                    Full Name
                  </label>
                  <input
                    id="full-name"
                    type="text"
                    placeholder="Enter interviewer's full name"
                    value={interviewerInfo.fullName || ''}
                    onChange={(e) => setInterviewerInfo(prev => ({ ...prev, fullName: e.target.value }))}
                    className={inputClasses}
                  />
                </div>
                
                <div>
                  <label htmlFor="job-title" className={labelClasses}>
                    Job Title
                  </label>
                  <div className="mb-2 text-sm text-blue-600 bg-blue-50 p-2 rounded-md">
                    <strong>Pro tip:</strong> The interviewer's exact job title helps us generate 
                    role-specific questions and insights for your interview preparation. You must have this information (ask your recruiter if you're working with one).
                  </div>
                  <input
                    id="job-title"
                    type="text"
                    placeholder="e.g. Senior Software Engineer, Product Manager"
                    value={interviewerInfo.jobTitle || ''}
                    onChange={(e) => setInterviewerInfo(prev => ({ ...prev, jobTitle: e.target.value }))}
                    className={inputClasses}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section 3: Job Information */}
      <div className="relative">
        <div className="absolute -left-12 top-0">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
            3
          </div>
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Job Information</h3>
          <div className="space-y-4">
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setJobMethod('url')}
                className={`flex-1 py-2 px-4 rounded-lg border ${
                  jobMethod === 'url' 
                    ? 'border-blue-600 bg-blue-50 text-blue-600' 
                    : 'border-gray-200'
                }`}
              >
                Job URL
              </button>
              <button
                type="button"
                onClick={() => setJobMethod('description')}
                className={`flex-1 py-2 px-4 rounded-lg border ${
                  jobMethod === 'description' 
                    ? 'border-blue-600 bg-blue-50 text-blue-600' 
                    : 'border-gray-200'
                }`}
              >
                Job Description
              </button>
            </div>

            {jobMethod === 'url' ? (
              <div>
                <label htmlFor="job-url" className={labelClasses}>
                  Job Posting URL
                </label>
                <input
                  id="job-url"
                  type="url"
                  placeholder="https://..."
                  value={jobInfo.jobUrl || ''}
                  onChange={(e) => setJobInfo({ jobUrl: e.target.value })}
                  className={inputClasses}
                />
                <div className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded-md">
                  <strong>Note:</strong> For job posts from ZipRecruiter, Monster, or Dice.com, please use the "Job Description" option and paste the job description directly.
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="job-description" className={labelClasses}>
                  Job Description
                </label>
                <textarea
                  id="job-description"
                  placeholder="Paste job description here..."
                  value={jobInfo.jobDescription || ''}
                  onChange={(e) => setJobInfo({ jobDescription: e.target.value })}
                  className={`${inputClasses} h-32`}
                />
              </div>
            )}

            <div>
              <label htmlFor="company-website" className={labelClasses}>
                Company Website <span className="text-red-500">*</span>
              </label>
              <input
                id="company-website"
                type="url"
                placeholder="https://company.com"
                value={jobInfo.companyWebsite || ''}
                onChange={(e) => setJobInfo(prev => ({ ...prev, companyWebsite: e.target.value }))}
                className={inputClasses}
                required
              />
            </div>
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
      >
        {loading ? 'Creating Report...' : 'Generate Report'}
      </button>
    </form>
  );
}